import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Plus, Search, ArrowLeft, User as UserIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Btn, toast } from './UI';

const ROLE_COLORS = {
  superadmin: '#8B5CF6',
  admin: '#3B82F6',
  client: '#10B981',
  professional: '#F59E0B',
  ambulance: '#EF4444',
};
const ROLE_LABELS = {
  superadmin: 'Super Admin',
  admin: 'Admin',
  client: 'Cliente',
  professional: 'Profesional',
  ambulance: 'Recurso',
};

const POLL_INTERVAL_CONV = 10000; // list conversations every 10s (with badge)
const POLL_INTERVAL_MSG = 5000;   // current conversation messages every 5s

/**
 * Floating chat panel. Opens from TopBar button (ChatBubble).
 * - Left: list of conversations with unread badges
 * - Right: messages of the selected conversation + send input
 * - New conversation picker
 * - HTTP polling (no WebSocket in MVP)
 */
export function ChatPanel({ open, onClose }) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, conversation, new
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const scrollRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const convs = await api.chatConversations();
      setConversations(convs);
    } catch (e) {
      console.error('load convs:', e.message);
    }
    setLoading(false);
  }, []);

  const loadMessages = useCallback(async (convId) => {
    try {
      const msgs = await api.chatMessages(convId);
      setMessages(msgs);
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch (e) {
      console.error('load messages:', e.message);
    }
  }, []);

  // Poll conversations while panel open
  useEffect(() => {
    if (!open) return;
    loadConversations();
    const t = setInterval(loadConversations, POLL_INTERVAL_CONV);
    return () => clearInterval(t);
  }, [open, loadConversations]);

  // Poll messages of active conversation
  useEffect(() => {
    if (!activeConv) return;
    loadMessages(activeConv.id);
    const t = setInterval(() => loadMessages(activeConv.id), POLL_INTERVAL_MSG);
    return () => clearInterval(t);
  }, [activeConv, loadMessages]);

  const openConv = (conv) => {
    setActiveConv(conv);
    setView('conversation');
    // Optimistically clear unread counter
    setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || !activeConv) return;
    setSending(true);
    const optimistic = {
      id: 'tmp_' + Date.now(),
      sender_id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      body,
      created_at: new Date().toISOString(),
      _tmp: true,
    };
    setMessages(prev => [...prev, optimistic]);
    setDraft('');
    setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    try {
      await api.chatSendMessage(activeConv.id, body);
      loadMessages(activeConv.id);
    } catch (e) {
      toast.error('No se pudo enviar', e.message);
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
    }
    setSending(false);
  };

  const startNew = async () => {
    setView('new');
    try {
      const list = await api.chatUsers();
      setUsers(list);
    } catch (e) { toast.error('Error', e.message); }
  };

  const createDirect = async (recipient) => {
    try {
      const { id, existed } = await api.chatCreateConversation({
        type: 'direct',
        user_ids: [recipient.id],
        title: `${recipient.first_name} ${recipient.last_name}`,
      });
      await loadConversations();
      openConv({ id, title: `${recipient.first_name} ${recipient.last_name}`, members: [recipient] });
      if (!existed) toast.success('Chat creado');
    } catch (e) { toast.error('Error', e.message); }
  };

  const convTitle = (c) => {
    if (c.title) return c.title;
    const other = (c.members || []).find(m => m.id !== user.id);
    return other ? `${other.first_name} ${other.last_name}` : 'Conversación';
  };

  const filteredUsers = users.filter(u => {
    if (!userSearch) return true;
    const q = userSearch.toLowerCase();
    return `${u.first_name} ${u.last_name} ${u.email || ''}`.toLowerCase().includes(q);
  });

  if (!open) return null;

  return (
    <div className="chat-panel-overlay" onClick={onClose}>
      <div className="chat-panel" onClick={e => e.stopPropagation()}>
        <header className="chat-panel-header">
          {view === 'conversation' && (
            <button onClick={() => { setView('list'); setActiveConv(null); }} className="chat-panel-back" aria-label="Volver">
              <ArrowLeft size={18} />
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <MessageCircle size={18} style={{ color: 'var(--accent)' }} />
            <span style={{ fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {view === 'conversation' ? convTitle(activeConv || {}) : view === 'new' ? 'Nuevo chat' : 'Chats'}
            </span>
          </div>
          <button onClick={onClose} className="chat-panel-close" aria-label="Cerrar"><X size={20} /></button>
        </header>

        {view === 'list' && (
          <>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
              <Btn size="sm" icon={Plus} onClick={startNew} style={{ width: '100%', justifyContent: 'center' }}>Nuevo chat</Btn>
            </div>
            <div className="chat-panel-body">
              {loading ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Cargando…</div>
              ) : conversations.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  <MessageCircle size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                  <div>Aún no tienes chats. Empieza uno con el botón de arriba.</div>
                </div>
              ) : (
                conversations.map(c => {
                  const other = (c.members || []).find(m => m.id !== user.id);
                  const otherColor = other ? ROLE_COLORS[other.role] : 'var(--accent)';
                  const initials = other ? (other.first_name?.[0] || '') + (other.last_name?.[0] || '') : '?';
                  return (
                    <button key={c.id} className="chat-conv-row" onClick={() => openConv(c)}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg, ${otherColor}, color-mix(in srgb, ${otherColor} 50%, var(--bg)))`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                        {initials.toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{convTitle(c)}</span>
                          {c.last_at && <span style={{ fontSize: 11, color: 'var(--text-dim)', flexShrink: 0 }}>{relTime(c.last_at)}</span>}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {c.last_body || 'Sin mensajes'}
                          </span>
                          {c.unread_count > 0 && (
                            <span className="chat-unread-badge">{c.unread_count}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}

        {view === 'new' && (
          <>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 22, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
              <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Buscar usuario..." style={{ paddingLeft: 30, fontSize: 13 }} autoFocus />
            </div>
            <div className="chat-panel-body">
              {filteredUsers.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Sin usuarios</div>
              ) : filteredUsers.map(u => (
                <button key={u.id} className="chat-conv-row" onClick={() => createDirect(u)}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: ROLE_COLORS[u.role] || 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                    {(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}
                  </div>
                  <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{u.first_name} {u.last_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{ROLE_LABELS[u.role] || u.role}{u.email ? ` · ${u.email}` : ''}</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {view === 'conversation' && activeConv && (
          <>
            <div className="chat-panel-body" ref={scrollRef}>
              {messages.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Sin mensajes. Escribe el primero.</div>
              ) : messages.map((m) => {
                const isMine = m.sender_id === user.id;
                return (
                  <div key={m.id} style={{ display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row', gap: 8, padding: '4px 14px', marginBottom: 6 }}>
                    {!isMine && (
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: ROLE_COLORS[m.role] || 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        {(m.first_name?.[0] || '') + (m.last_name?.[0] || '')}
                      </div>
                    )}
                    <div style={{
                      maxWidth: '70%',
                      padding: '8px 12px',
                      borderRadius: 14,
                      background: isMine ? 'var(--accent)' : 'var(--surface-alt)',
                      color: isMine ? '#fff' : 'var(--text)',
                      fontSize: 13.5,
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                      opacity: m._tmp ? 0.6 : 1,
                    }}>
                      {!isMine && <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, marginBottom: 2 }}>{m.first_name} {m.last_name}</div>}
                      {m.body}
                      <div style={{ fontSize: 10, opacity: 0.6, marginTop: 3, textAlign: isMine ? 'right' : 'left' }}>
                        {new Date(m.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={e => { e.preventDefault(); send(); }} className="chat-panel-input-row">
              <input
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder="Escribe un mensaje…"
                autoFocus
                disabled={sending}
              />
              <Btn type="submit" icon={Send} disabled={!draft.trim() || sending}>Enviar</Btn>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// Floating launcher button (shown in TopBar). Shows unread badge.
export function ChatLauncher() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const refreshUnread = useCallback(async () => {
    try {
      const r = await api.chatUnread();
      setUnread(r.count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 15000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  return (
    <>
      <button
        onClick={() => { setOpen(true); refreshUnread(); }}
        title="Chat"
        style={{
          position: 'relative', background: 'none', border: 'none',
          padding: 8, borderRadius: 8, cursor: 'pointer',
          color: 'var(--text-muted)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <MessageCircle size={20} />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            background: '#EF4444', color: '#fff',
            fontSize: 10, fontWeight: 800,
            minWidth: 16, height: 16, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px',
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
      <ChatPanel open={open} onClose={() => { setOpen(false); refreshUnread(); }} />
    </>
  );
}

function relTime(iso) {
  const diffS = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diffS < 60) return 'ahora';
  if (diffS < 3600) return `${Math.floor(diffS/60)}m`;
  if (diffS < 86400) return `${Math.floor(diffS/3600)}h`;
  if (diffS < 7 * 86400) return `${Math.floor(diffS/86400)}d`;
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
}
